'use client'

import {
  INewsFields,
  IReviewFields,
  INewsEntry,
  IReviewEntry,
  IFaqEntry,
  IFaqFields,
  ContentfulListResponse,
  ContentfulAsset,
  IFaqAsset,
} from '@/types/contentful'
import { fetchContentfulData, fetchContentfulFaqData, fetchContentfulFaqDetail, fetchContentfulNoticeDetail, fetchContentfulTermsData } from '@/utils/contentful'
import { buildAssetsById, mapAssetsToSimple, resolveAssetUrl } from '@/utils/contentfulAsset'
import { useQuery } from '@tanstack/react-query'
import type {
  ITermsEntry,
  ITermsWithId,
  ContentfulEntry,
  ContentfulListResponseWithIncludes,
  INoticeFields,
} from '@/types/contentful'

export const useReviewsDataQuery = (contentType: string) => {
  return useQuery<IReviewFields[], Error>({
    queryKey: ['reviews'],
    queryFn: async () => {
      const data = await fetchContentfulData(contentType)
      if (data) {
        return data.items.map((item: IReviewEntry) => item.fields)
      } else {
        throw new Error('Failed to fetch reviews')
      }
    }
  })
}

export const useNewsDataQuery = (contentType: string) => {
  return useQuery<INewsFields[], Error>({
    queryKey: ['news'],
    queryFn: async () => {
      const data = await fetchContentfulData(contentType)
      if (data) {
        return data.items.map((item: INewsEntry) => item.fields)
      } else {
        throw new Error('Failed to fetch news')
      }
    }
  })
}


export const useFaqDataQuery = (contentType: string, category: string) => {
  return useQuery<{ faqs: IFaqFields[] }, Error>({
    queryKey: ['faq', contentType, category],
    queryFn: async () => {
      const data = (await fetchContentfulFaqData(contentType, category)) as ContentfulListResponse<IFaqEntry>
      if (!data) throw new Error('Failed to fetch faq')

      const faqs: IFaqFields[] = data.items.map((item: IFaqEntry) => ({ ...item.fields, id: item.sys.id }))
      return { faqs }
    }
  })
}

export const useFaqDetailDataQuery = (id: string) => {
  return useQuery<{ faq: IFaqFields, assets: IFaqAsset[] }, Error>({
    queryKey: ['faq', id],
    queryFn: async () => {
      const data = await fetchContentfulFaqDetail(id)
      if (!data) throw new Error('Failed to fetch faq detail')

      const faq = data.items[0].fields
      const assets = mapAssetsToSimple((data.includes?.Asset ?? []) as ContentfulAsset[])


      return { faq, assets }
    }
  })
}

export const useTermsDataQuery = (contentType: string, category: string) => {
  return useQuery<{ terms: ITermsWithId[]; entriesById: Record<string, ContentfulEntry> }, Error>({
    queryKey: ['terms', category],
    queryFn: async () => {
      const data = (await fetchContentfulTermsData(contentType, category)) as ContentfulListResponseWithIncludes<ITermsEntry>
      if (!data) throw new Error('Failed to fetch terms')

      const terms: ITermsWithId[] = data.items.map((item) => ({ id: item.sys.id, ...item.fields }))

      const entriesById = (data.includes?.Entry ?? []).reduce((acc: Record<string, ContentfulEntry>, cur) => {
        acc[cur.sys.id] = cur
        return acc
      }, {})
      return { terms, entriesById }
    }
  })
}

export const useNoticeDataQuery = (contentType: string) => {
  return useQuery<INoticeFields[], Error>({
    queryKey: ['notice', contentType],
    queryFn: async () => {
      const data = await fetchContentfulData(contentType, { include: 2 })
      if (!data) throw new Error('Failed to fetch notice')

      const assetsById = buildAssetsById((data.includes?.Asset ?? []) as ContentfulAsset[])

      type NoticeFieldsFromContentful = Omit<INoticeFields, 'id' | 'thumbnail'> & { thumbnail?: unknown }

      const notices = (data.items ?? []).map((item: { sys: { id: string }; fields: NoticeFieldsFromContentful }) => {
        const fields = item.fields
        const rawThumbnail = (fields as unknown as { thumbnail?: unknown })?.thumbnail

        const url = resolveAssetUrl(rawThumbnail, assetsById)

        return {
          ...fields,
          id: item.sys.id,
          thumbnail: url,
        }
      })

      const toTime = (value?: string) => {
        if (!value) return 0
        const t = new Date(value).getTime()
        return Number.isFinite(t) ? t : 0
      }

      // 최신 authoredAt이 먼저 오도록 정렬
      return notices.sort((a: INoticeFields, b: INoticeFields) => toTime(b.authoredAt) - toTime(a.authoredAt))
    }
  })
}

export const useNoticeDetailQuery = (id: string) => {
  return useQuery<{ notice: INoticeFields; assets: IFaqAsset[] }, Error>({
    queryKey: ['notice', 'detail', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const data = await fetchContentfulNoticeDetail(id, { include: 2 })
      if (!data) throw new Error('Failed to fetch notice detail')

      const entry = (data.items?.[0] ?? null) as null | { sys?: { id?: string }, fields?: unknown }
      if (!entry?.fields) throw new Error('Notice not found')

      const includedAssets = (data.includes?.Asset ?? []) as ContentfulAsset[]
      const assets = mapAssetsToSimple(includedAssets)
      const assetsById = buildAssetsById(includedAssets)

      type NoticeFieldsFromContentful = Omit<INoticeFields, 'id' | 'thumbnail'> & { thumbnail?: unknown }
      const fields = entry.fields as NoticeFieldsFromContentful
      const rawThumbnail = (fields as unknown as { thumbnail?: unknown })?.thumbnail
      const url = resolveAssetUrl(rawThumbnail, assetsById)

      return {
        notice: {
          ...fields,
          id: entry.sys?.id ?? id,
          thumbnail: url,
        },
        assets,
      }
    },
  })
}